import { useState, useEffect } from "react";
import { Box, Slider, Typography, Paper, Button } from "@mui/material";
import axios from "axios";
import ImageIcon from "@mui/icons-material/Image";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ImageBrowserPopup from "../PreviousImagePopup/popup"; // Adjust the path as needed

export default function EyeshadowApp() {
  const [image, setImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [eyeshadowIntensity, setEyeshadowIntensity] = useState(50);
  const [color, setColor] = useState({ r: 0, g: 0, b: 0 });
  const [scanning, setScanning] = useState(false);
  const [imageBrowserOpen, setImageBrowserOpen] = useState(false);

  // When the image or its parameters change, process the image
  useEffect(() => {
    if (image) {
      const timer = setTimeout(() => {
        processImage(image);
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line
  }, [eyeshadowIntensity, color, image]);

  // On component mount, check for a stored image URL and load it if available
  useEffect(() => {
    const storedImageUrl = localStorage.getItem("selectedImageUrl");
    if (storedImageUrl && !image) {
      handlePreviousImageSelect({ url: storedImageUrl });
    }
    // eslint-disable-next-line
  }, []);

  // Handles image selection from the popup and stores the image URL
  const handlePreviousImageSelect = async (selectedImage) => {
    setScanning(true);
    try {
      // Persist the selected image URL so it's shared across pages
      localStorage.setItem("selectedImageUrl", selectedImage.url);

      const response = await fetch(selectedImage.url);
      const blob = await response.blob();
      const file = new File([blob], "selected_image.jpg", {
        type: "image/jpeg",
      });
      setProcessedImage(URL.createObjectURL(file));
      setImage(file);
    } catch (error) {
      console.error("Error processing previous image:", error);
      toast.error("Error processing previous image.", {
        position: "bottom-center",
        autoClose: 3000,
      });
    }
    setImageBrowserOpen(false);
    setScanning(false);
  };

  // Processes the image using the eyeshadow API
  const processImage = async (file) => {
    setScanning(true);
    try {
      // Step 1: Upload the file to get an intermediate preview
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_API_KEY}/makeup/upload`,
        formDataUpload,
        {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        }
      );
      if (!uploadResponse.data) {
        throw new Error("Upload API did not return image data");
      }
      const intermediateImageUrl = URL.createObjectURL(uploadResponse.data);
      setProcessedImage(intermediateImageUrl);

      // Delay for scanning animation (3 seconds)
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Step 2: Apply eyeshadow using the API
      const formData = new FormData();
      formData.append("file", file);
      formData.append("eyeshadow_color", `${color.b},${color.g},${color.r}`);
      formData.append(
        "eyeshadow_intensity",
        (eyeshadowIntensity / 100).toString()
      );
      const url = `${process.env.REACT_APP_API_KEY}/makeup/apply_eyeshadow`;
      const response = await axios.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
      });
      if (!response.data) {
        throw new Error("Eyeshadow API did not return image data");
      }
      setProcessedImage(URL.createObjectURL(response.data));
    } catch (error) {
      console.error("Error processing image:", error);
      toast.error("Error processing image. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
      });
    }
    setScanning(false);
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 4,
        p: 4,
        pt: 16,
        pb: 16,
        minHeight: "100vh",
        width: "100%",
        bgcolor: "#0d1117",
        fontFamily: "Poppins, sans-serif",
        color: "#fff",
      }}
    >
      {/* Image Browser Popup */}
      <ImageBrowserPopup
        open={imageBrowserOpen}
        onClose={() => setImageBrowserOpen(false)}
        onImageSelect={handlePreviousImageSelect}
      />

      {/* Processed Image Display with Scanning Overlay */}
      <Paper
        elevation={6}
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 3,
          background: "rgba(255,255,255,0.05)",
          backdropFilter: "blur(12px)",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
          height: { xs: "auto", md: "80vh" },
          width: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {processedImage ? (
          <>
            <img
              src={processedImage}
              alt="Processed"
              style={{
                maxWidth: "100%",
                maxHeight: "calc(100% - 60px)",
                borderRadius: "12px",
                transition: "0.3s ease",
                boxShadow: "0px 5px 20px rgba(255,255,255,0.1)",
              }}
            />
            {scanning && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "110%",
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              >
                {/* Horizontal scanning line */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    width: "100%",
                    height: "3px",
                    background:
                      "linear-gradient(90deg, transparent, #00ffff, #ff00ff, transparent)",
                    boxShadow:
                      "0 0 10px #00ffff, 0 0 20px #00ffff, 0 0 30px #00ffff",
                    animation: "scanHorizontal 2s ease-in-out infinite",
                    "@keyframes scanHorizontal": {
                      "0%": { top: "10%", opacity: 0 },
                      "15%": { opacity: 1 },
                      "85%": { opacity: 1 },
                      "100%": { top: "90%", opacity: 0 },
                    },
                  }}
                />
                {/* Vertical scanning line */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    height: "100%",
                    width: "3px",
                    background:
                      "linear-gradient(0deg, transparent, #00ffff, #ff00ff, transparent)",
                    boxShadow:
                      "0 0 10px #00ffff, 0 0 20px #00ffff, 0 0 30px #00ffff",
                    animation: "scanVertical 2s ease-in-out infinite",
                    animationDelay: "1s",
                    "@keyframes scanVertical": {
                      "0%": { left: "10%", opacity: 0 },
                      "15%": { opacity: 1 },
                      "85%": { opacity: 1 },
                      "100%": { left: "90%", opacity: 0 },
                    },
                  }}
                />
                {/* Pulsing overlay effect */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background:
                      "radial-gradient(circle, transparent, rgba(0,255,255,0.05))",
                    animation: "pulse 1.5s ease-in-out infinite",
                    "@keyframes pulse": {
                      "0%": { opacity: 0.1 },
                      "50%": { opacity: 0.3 },
                      "100%": { opacity: 0.1 },
                    },
                  }}
                />
                {/* Scanning status text */}
                <Typography
                  sx={{
                    position: "absolute",
                    bottom: "60px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    color: "#00ffff",
                    fontSize: "18px",
                    fontWeight: "bold",
                    textShadow: "0 0 10px #00ffff",
                    animation: "blink 1s infinite",
                    "@keyframes blink": {
                      "0%": { opacity: 0.5 },
                      "50%": { opacity: 1 },
                      "100%": { opacity: 0.5 },
                    },
                  }}
                >
                  Applying eyeshadow...
                  <br />
                  Getting your glam look!
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <Typography sx={{ color: "#ccc", fontSize: 18 }}>
            {scanning
              ? "Scanning image..."
              : "Upload an image to apply eyeshadow"}
          </Typography>
        )}
      </Paper>

      {/* Controls */}
      <Paper
        elevation={6}
        sx={{
          p: 4,
          borderRadius: 3,
          background: "rgba(255,255,255,0.05)",
          backdropFilter: "blur(12px)",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
          height: { xs: "auto", md: "80vh" },
          display: "flex",
          flexDirection: "column",
          gap: 3,
          color: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="contained"
            startIcon={<ImageIcon />}
            sx={{
              background: "#a41847",
              color: "#fff",
              textTransform: "none",
              borderRadius: "8px",
              padding: "10px 20px",
              "&:hover": { background: "#860f37" },
            }}
            onClick={() => {
              setImageBrowserOpen(true);
              localStorage.setItem("imageType", "uploaded");
            }}
          >
            Uploaded Images
          </Button>
          <Button
            variant="contained"
            startIcon={<ImageIcon />}
            sx={{
              background: "#a41847",
              color: "#fff",
              textTransform: "none",
              borderRadius: "8px",
              padding: "10px 20px",
              "&:hover": { background: "#860f37" },
            }}
            onClick={() => {
              setImageBrowserOpen(true);
              localStorage.setItem("imageType", "captured");
            }}
          >
            Captured Images
          </Button>
          <Button
            variant="contained"
            startIcon={<ImageIcon />}
            sx={{
              background: "#a41847",
              color: "#fff",
              textTransform: "none",
              borderRadius: "8px",
              padding: "10px 20px",
              "&:hover": { background: "#860f37" },
            }}
            onClick={() => {
              setImageBrowserOpen(true);
              localStorage.setItem("imageType", "model");
            }}
          >
            Model Image
          </Button>
        </Box>

        <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
          Eyeshadow Intensity
        </Typography>
        <Slider
          value={eyeshadowIntensity}
          min={0}
          max={100}
          onChange={(e, newValue) => setEyeshadowIntensity(Number(newValue))}
          sx={{ color: "#a41847", height: 6 }}
        />

        <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
          Eyeshadow Color
        </Typography>
        <input
          type="color"
          onChange={(e) => {
            const hex = e.target.value;
            setColor({
              r: parseInt(hex.slice(1, 3), 16),
              g: parseInt(hex.slice(3, 5), 16),
              b: parseInt(hex.slice(5, 7), 16),
            });
          }}
          style={{
            width: "100%",
            height: 50,
            border: "none",
            cursor: "pointer",
            borderRadius: "6px",
            background: "none",
            boxShadow: "0px 3px 12px rgba(255,255,255,0.1)",
          }}
        />
      </Paper>
      <ToastContainer />
    </Box>
  );
}
